import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const WORDS_PER_TICK = 1;
const TICK_INTERVAL_MS = 30;

const countWords = (html: string): number => {
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent ?? "";
  return text.trim().split(/\s+/).filter(Boolean).length;
};

const truncateToWords = (html: string, maxWords: number): string => {
  const div = document.createElement("div");
  div.innerHTML = html;
  let remaining = maxWords;

  const walk = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const words = node.textContent!.split(/(\s+)/);
      let result = "";
      for (const part of words) {
        if (/\S/.test(part)) {
          if (remaining <= 0) {
            node.textContent = result;
            return true;
          }
          remaining--;
        }
        result += part;
      }
      node.textContent = result;
      return remaining <= 0;
    }

    const children = Array.from(node.childNodes);
    for (let i = 0; i < children.length; i++) {
      if (walk(children[i])) {
        // Remove remaining siblings
        for (let j = children.length - 1; j > i; j--) {
          node.removeChild(children[j]);
        }
        return true;
      }
    }
    return false;
  };

  walk(div);
  return div.innerHTML;
};

const useTypewriterAnimation = (
  content: string,
  generating: boolean,
  enabled: boolean = true
): { animating: boolean; skipNextAnimation: () => void; visibleHtml: string } => {
  const [animating, setAnimating] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const totalWordsRef = useRef(0);
  const prevContentRef = useRef("");
  const skipNextAnimationRef = useRef(false);

  const skipNextAnimation = useCallback(() => {
    skipNextAnimationRef.current = true;
  }, []);

  useEffect(() => {
    if (!enabled || generating || !content || content === prevContentRef.current) {
      return;
    }

    prevContentRef.current = content;

    if (skipNextAnimationRef.current) {
      skipNextAnimationRef.current = false;
      return;
    }

    const total = countWords(content);
    if (!total) {
      return;
    }

    totalWordsRef.current = total;
    setWordCount(0);
    setAnimating(true);
  }, [content, enabled, generating]);

  useEffect(() => {
    if (!animating) {
      return;
    }

    const timer = setInterval(() => {
      setWordCount((prev) => {
        const next = prev + WORDS_PER_TICK;
        if (next >= totalWordsRef.current) {
          clearInterval(timer);
          setAnimating(false);
          return totalWordsRef.current;
        }
        return next;
      });
    }, TICK_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [animating]);

  const visibleHtml = useMemo(() => {
    if (!animating) {
      return content;
    }
    return truncateToWords(content, wordCount);
  }, [animating, content, wordCount]);

  return { animating, skipNextAnimation, visibleHtml };
};

export default useTypewriterAnimation;
